// ============================================================
//  SUPPORT PAGE — ADMIN DASHBOARD
//  Path: src/pages/SupportPage.tsx
//
//  NEW FILE. Where help requests from the expert and customer apps land,
//  and where staff write back.
//
//   - Each request shows who, which app, the topic, what they typed, and
//     where they were stuck (screen, and the Stripe page heading when it
//     came from Stripe Setup), plus app version and platform.
//   - "Reply in app" sends a one-way message into the person's Messages
//     inbox (with a push) and marks the request answered. An optional
//     button can open payout setup, documents, etc. in their app.
//   - "New message" writes to anyone by email address, no request needed.
//   - Email replies also work: the support inbox email carries Reply-To.
//
//  CHANGE (2026-10-10): opening /support?search=<userId>&appType=expert
//  (the "open requests" note on a person's page) starts already filtered to
//  that person. After every load the open count is announced, so the red
//  number on the Support menu item updates straight away.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  listSupportRequestsApi, updateSupportRequestApi, sendSupportMessageApi, getSupportOptionsApi, announceSupportOpenCount,
  SupportRequestRow, SupportOptions, SupportStatus,
} from '../api/support.api'

const STATUS_TABS: Array<{ key: string; label: string }> = [
  { key: 'open', label: 'Open' }, { key: 'answered', label: 'Answered' }, { key: 'closed', label: 'Closed' }, { key: '', label: 'All' },
]

const TOPIC_LABEL: Record<string, string> = {
  stripe_setup: 'Stripe setup', payout: 'Payout / money', documents: 'Documents',
  account: 'Account', payment: 'Payment', task: 'A job', other: 'Other',
}

const STATUS_STYLE: Record<SupportStatus, string> = {
  open:     'bg-orange-500/10 text-orange-400 border-orange-500/30',
  answered: 'bg-green-500/10 text-green-400 border-green-500/30',
  closed:   'bg-gray-500/10 text-gray-400 border-gray-500/30',
}

const errText = (e: any, fallback: string) => e?.response?.data?.message || fallback
const fmt = (iso: string) => new Date(iso).toLocaleString()

type Compose = { request: SupportRequestRow | null } | null

const SupportPage: React.FC = () => {
  // Starting filters can come from the link (see header).
  const [params] = useSearchParams()
  const [status, setStatus] = useState(() => {
    const s = params.get('status')
    return s === null ? 'open' : (['open', 'answered', 'closed', ''].includes(s) ? s : 'open')
  })
  const [appType, setAppType] = useState(() => (['expert', 'customer'].includes(params.get('appType') || '') ? (params.get('appType') as string) : ''))
  const [search, setSearch] = useState(() => (params.get('search') || '').slice(0, 100))
  const [page, setPage] = useState(1)
  const [rows, setRows] = useState<SupportRequestRow[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [openCount, setOpenCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [options, setOptions] = useState<SupportOptions | null>(null)
  const [compose, setCompose] = useState<Compose>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await listSupportRequestsApi({ status: status || undefined, appType: appType || undefined, search: search.trim() || undefined, page, limit: 20 })
      setRows(res.data?.data ?? [])
      setTotal(res.data?.pagination?.total ?? 0)
      setTotalPages(Math.max(1, res.data?.pagination?.totalPages ?? 1))
      setOpenCount(res.data?.openCount ?? 0)
      announceSupportOpenCount(res.data?.openCount ?? 0)
    } catch (e) {
      setError(errText(e, 'Could not load requests'))
    } finally {
      setLoading(false)
    }
  }, [status, appType, search, page])

  useEffect(() => { load() }, [load])
  useEffect(() => { getSupportOptionsApi().then((r) => setOptions(r.data?.data ?? null)).catch(() => {}) }, [])

  const setRequestStatus = async (row: SupportRequestRow, next: SupportStatus) => {
    setBusyId(row.id)
    try {
      await updateSupportRequestApi(row.id, { status: next })
      await load()
    } catch (e) {
      setError(errText(e, 'Could not update the request'))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Support</h1>
          <p className="text-gray-400 text-sm mt-1">{openCount} open · {total} shown</p>
        </div>
        <button onClick={() => setCompose({ request: null })}
          className="px-4 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-xl text-sm font-medium transition-colors">
          New message
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex gap-2">
          {STATUS_TABS.map((t) => (
            <button key={t.label} onClick={() => { setStatus(t.key); setPage(1) }}
              className={`px-4 py-2 rounded-xl text-sm transition-colors ${status === t.key ? 'bg-brand-500 text-white' : 'bg-surface-tertiary text-gray-300 hover:bg-border-light'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <select value={appType} onChange={(e) => { setAppType(e.target.value); setPage(1) }}
          className="bg-surface-tertiary border border-border-light rounded-xl px-4 py-2 text-white focus:border-brand-500 focus:outline-none">
          <option value="">Both apps</option>
          <option value="expert">Experts</option>
          <option value="customer">Customers</option>
        </select>
        <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} placeholder="Search message, email, Stripe page…"
          className="flex-1 min-w-[200px] bg-surface-tertiary border border-border-light rounded-xl px-4 py-2 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none" />
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 mb-6 flex items-center justify-between gap-3">
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={load} className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light shrink-0">Retry</button>
        </div>
      )}

      <div className="space-y-4">
        {loading ? (
          <p className="text-center py-8 text-gray-400">Loading...</p>
        ) : rows.length === 0 ? (
          <p className="text-center py-8 text-gray-400">No requests here.</p>
        ) : rows.map((r) => (
          <div key={r.id} className="bg-surface-card border border-border-light rounded-2xl p-5">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className={`px-2.5 py-1 rounded-lg text-xs border ${STATUS_STYLE[r.status]}`}>{r.status}</span>
              <span className="px-2.5 py-1 rounded-lg text-xs bg-surface-tertiary text-gray-300">{r.appType === 'expert' ? 'Expert' : 'Customer'}</span>
              <span className="px-2.5 py-1 rounded-lg text-xs bg-surface-tertiary text-gray-300">{TOPIC_LABEL[r.topic] || r.topic}</span>
              <span className="text-xs text-gray-500 ml-auto">{fmt(r.createdAt)}</span>
            </div>

            <p className="text-white font-medium">{r.userName || 'Unknown name'}
              <span className="text-gray-400 font-normal text-sm"> · {r.contactEmail || 'no email'}{r.contactPhone ? ` · ${r.contactPhone}` : ''}</span>
            </p>
            <p className="text-gray-200 mt-3 whitespace-pre-wrap">{r.message}</p>

            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500">
              {r.pageHeading && <span>Stripe page: <span className="text-gray-300">{r.pageHeading}</span></span>}
              {r.screenName && <span>Screen: <span className="text-gray-300">{r.screenName}</span></span>}
              {r.appVersion && <span>Version: <span className="text-gray-300">{r.appVersion}{r.platform ? ` (${r.platform})` : ''}</span></span>}
              {!r.emailSent && <span className="text-orange-400">Not emailed to the support inbox</span>}
            </div>
            {r.adminNote && <p className="mt-3 text-sm text-gray-400 border-l-2 border-border-light pl-3">Note: {r.adminNote}</p>}

            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => setCompose({ request: r })}
                className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-xl text-sm transition-colors">Reply in app</button>
              {r.status !== 'closed' && (
                <button disabled={busyId === r.id} onClick={() => setRequestStatus(r, 'closed')}
                  className="px-4 py-2 bg-surface-tertiary text-gray-300 hover:bg-border-light rounded-xl text-sm transition-colors disabled:opacity-50">Close</button>
              )}
              {r.status !== 'open' && (
                <button disabled={busyId === r.id} onClick={() => setRequestStatus(r, 'open')}
                  className="px-4 py-2 bg-surface-tertiary text-gray-300 hover:bg-border-light rounded-xl text-sm transition-colors disabled:opacity-50">Reopen</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-40">Previous</button>
          <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-40">Next</button>
        </div>
      )}

      {compose && (
        <ComposeModal
          request={compose.request}
          options={options}
          onClose={() => setCompose(null)}
          onSent={() => { setCompose(null); load() }}
        />
      )}
    </div>
  )
}

// ── Write a message ──────────────────────────────────────────
const ComposeModal: React.FC<{
  request: SupportRequestRow | null
  options: SupportOptions | null
  onClose: () => void
  onSent: () => void
}> = ({ request, options, onClose, onSent }) => {
  const [email, setEmail] = useState('')
  const [title, setTitle] = useState(request ? 'About your request' : '')
  const [body, setBody] = useState('')
  const [action, setAction] = useState('')
  const [sending, setSending] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // The button list depends on which app the person is in. For a new
  // message to an email address we do not know the app yet, so offer the
  // expert list (the server rejects it for a customer-only account).
  const actionList = options?.actionScreens?.[request?.appType ?? 'expert'] ?? {}
  const canSend = title.trim() && body.trim() && (request || email.trim()) && !sending

  const send = async () => {
    setSending(true)
    setErr(null)
    try {
      await sendSupportMessageApi({
        ...(request ? { userId: request.userId, requestId: request.id } : { email: email.trim() }),
        title: title.trim(), body: body.trim(), ...(action ? { actionScreen: action } : {}),
      })
      onSent()
    } catch (e) {
      setErr(errText(e, 'Could not send the message'))
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-white mb-1">{request ? `Reply to ${request.userName || 'this person'}` : 'New message'}</h2>
        <p className="text-xs text-gray-500 mb-4">Goes into their Messages inbox in the app, with a notification. They cannot reply there.</p>

        {!request && (
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Their email address"
            className="w-full mb-3 bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none" />
        )}
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Title"
          className="w-full mb-3 bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} rows={6} placeholder="Message — short and clear"
          className="w-full mb-1 bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none" />
        <p className="text-xs text-gray-500 mb-3 text-right">{body.length}/2000</p>

        {Object.keys(actionList).length > 0 && (
          <select value={action} onChange={(e) => setAction(e.target.value)}
            className="w-full mb-3 bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white focus:border-brand-500 focus:outline-none">
            <option value="">No button</option>
            {Object.entries(actionList).map(([screen, label]) => <option key={screen} value={screen}>Button: {label}</option>)}
          </select>
        )}

        {err && <p className="text-sm text-red-400 mb-3">{err}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-surface-tertiary text-gray-300 hover:bg-border-light rounded-xl text-sm">Cancel</button>
          <button disabled={!canSend} onClick={send}
            className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-xl text-sm disabled:opacity-50">{sending ? 'Sending…' : 'Send'}</button>
        </div>
      </div>
    </div>
  )
}

export default SupportPage